import React, { useState, useEffect, useContext } from 'react';
import { ConnectContext } from '../context/connectContext';
import { Text, Div, Row, Col, Modal, Input, Button, Icon, Switch, Anchor, Dropdown, Image } from 'atomize';
import { Link } from 'react-router-dom';
import styles from './styles.module.css'
import { Bar, Doughnut } from 'react-chartjs-2';

const HomePage = () => {
    const { resetContext, fetchAllPartners, partners, searchParam, setSearchParam, elos, fetchContactUrl, fetchAllELOs, loading, loadingAllELO, isPartnerSearch, setIsPartnerSearch } = useContext(ConnectContext);

    // image loading state for individual thumbnails
    const [loadedImages, setLoadedImages] = useState({});

    // tearsheet state management
    const [isCareerClusterOpen, setIsCareerClusterOpen] = useState(false);
    const [careerCluster, setCareerCluster] = useState(null);
    const [isEloTypeOpen, setIsEloTypeOpen] = useState(false);
    const [eloType, setEloType] = useState(null);
    const [isEloLocationOpen, setIsEloLocationOpen] = useState(false);
    const [eloLocation, setEloLocation] = useState(null);
    const [elo, setElo] = useState(null);
    const [isEloOpen, setIsEloOpen] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState("Cumulative");
    const [selectedYear, setSelectedYear] = useState("All");
    const [imageUrlByPartner, setImageUrlByPartner] = useState('');

    let filteredPartners = partners;
    let filteredELOs = elos;

    // set search bar state
    const handleInputChange = (input) => { 
        setSearchParam(input.toLowerCase());
    }
    if (searchParam !== '') {
        filteredPartners = filteredPartners.filter((partner) => partner["Name"].toLowerCase().includes(searchParam.toLowerCase()) || partner["ZIP"].toLowerCase().includes(searchParam.toLowerCase()) || partner["Description"].toLowerCase().includes(searchParam.toLowerCase()) || partner["County"].toLowerCase().includes(searchParam.toLowerCase()));
    }

    // filering on filter states
    if (careerCluster !== null) {
        filteredPartners =  filteredPartners.filter((partner) => partner[careerCluster] === "TRUE");
        filteredELOs = filteredELOs.filter((elo) => elo["Career Cluster"] === careerCluster);
    }
    if (eloType !== null) {
        filteredPartners =  filteredPartners.filter((partner) => partner[eloType] === "TRUE");
        if (eloType === "Field Site Visit" || eloType === "Curiosity Lab" || eloType === "Career Talk" || eloType === "Networking") {
            filteredELOs = filteredELOs.filter((elo) => elo["Interest Exploration"] === eloType);
        }
        else if (eloType === "Mini Pursuit" || eloType === "Personal Pursuit" || eloType === "Partner Pursuit" || eloType === "Micro Pursuit") {
            filteredELOs = filteredELOs.filter((elo) => elo["Pursuit Partnership"] === eloType);
        }
        else if (eloType === "Job Shadow" || eloType === "Internship" || eloType === "Externship" || eloType === "Employment") {
            filteredELOs = filteredELOs.filter((elo) => elo["Work Based Learning"] === eloType);
        }
        else {
            filteredELOs = filteredELOs.filter((elo) => elo["Community Embedded"] === eloType);
        }
    }
    if (eloLocation !== null) {
        filteredPartners =  filteredPartners.filter((partner) => partner[eloLocation] === "TRUE");
        filteredELOs = filteredELOs.filter((elo) => elo["ELO Location"] === eloLocation);
    }
    if (elo !== null) {
        filteredPartners =  filteredPartners.filter((partner) => partner[elo] === "TRUE");
        filteredELOs = filteredELOs.filter((eloRow) => eloRow["Learning Hub"] === elo);
    }

    // filtering on term/year
    if (selectedTerm !== "Cumulative") {
        filteredELOs = filteredELOs.filter((elo) => elo["ELO Term"] === selectedTerm);
    }
    if (selectedYear !== "All") {
        filteredELOs = filteredELOs.filter((elo) => elo["ELO Year"] === selectedYear);
    }
    
    const sumLearnersServed = (eloArray) => {
        return eloArray.reduce((sum, elo) => {
            return sum + Number(elo["Learners Served"] || 0);
        }, 0);
    }

    // set filter states
    const onSelectCareerCluster = (ClusterMapping) => {
        setCareerCluster(ClusterMapping);
        setIsCareerClusterOpen(!isCareerClusterOpen);
    }

    const onSelectEloType = (eloTypeMapping) => {
        setEloType(eloTypeMapping);
        setIsEloTypeOpen(!isEloTypeOpen);
    }

    const onSelectEloLocation = (eloLocationMapping) => {
        setEloLocation(eloLocationMapping);
        setIsEloLocationOpen(!isEloLocationOpen);
    }

    const onSelectElo = (eloMapping) => {
        setElo(eloMapping);
        setIsEloOpen(!isEloOpen);
    }

    // for removing a tag filter
    const onSpliceSelectedFilters = (input) => {
        if (input.includes("Career Cluster")) {
            setCareerCluster(null);
        }
        if (input.includes("Elo Type")) {
            setEloType(null);
        }
        if (input.includes("Elo Location")) {
            setEloLocation(null);
        }
        if (input.includes("Learning Hub")) {
            setElo(null);
        }
    }

    // splice filters
    const selectedFilters=[];
    if (careerCluster !== null) {
        selectedFilters.push("Career Cluster: " + careerCluster);
    }
    if (eloType !== null) {
        selectedFilters.push("Elo Type: " + eloType);
    }
    if (eloLocation !== null) {
        selectedFilters.push("Elo Location: " + eloLocation);
    }
    if (elo !== null) {
        selectedFilters.push("Learning Hub: " + elo);
    }

    // year dropdown state management
    const [showTermDropdown, setShowTermDropdown] = useState(false);
    const [showYearDropdown, setShowYearDropdown] = useState(false);

    // term dropdown list
    const termList = (
        <Div
            p={{ x: "2rem", y: "0.5rem" }}
            bg="white"
            rounded="lg"
            shadow="3"
        >
            {["Cumulative", "T1", "T2", "T3", "Summer"].map((term) => (
            <Anchor key={term} textColor="#2d3748" hoverTextColor="#5b9dd0" d="block" p={{ y: "0.25rem" }} onClick={() => setSelectedTerm(term)}>
                {term}
            </Anchor>
            ))}
        </Div>
    );

    // generate school years
    const generateSchoolYears = (startYear, numberOfYears) => {
        const years = [];
        for (let i = 0; i < numberOfYears; i++) {
            const year1 = startYear + i;
            const year2 = year1 + 1;
            years.unshift(`${year1} - ${year2}`); // Use unshift to add years to the start of the array
        }
        return years;
    };

    // filter out future school years
    const filterFutureYears = (years) => {
        const currentYear = new Date().getFullYear();
        return years.filter(year => {
        const [startYear, endYear] = year.split(' - ').map(Number);
        return startYear <= currentYear && endYear <= currentYear + 1;
        });
    };
    const allYears = generateSchoolYears(2023, 30);
    let validYears = filterFutureYears(allYears);
    validYears = ["All", ...validYears];

    // year dropdown list
    const sortList = (
        <Div
            p={{ x: "2rem", y: "0.5rem" }}
            bg="white"
            rounded="lg"
            shadow="3"
        >
            {validYears.map((year) => (
            <Anchor key={year} textColor="#2d3748" hoverTextColor="#5b9dd0" d="block" p={{ y: "0.25rem" }} onClick={() => setSelectedYear(year)}>
                {year}
            </Anchor>
            ))}
        </Div>
    );

    // colors for both graphs
    const colors = [
        "#5b9dd0", // Light Blue
        "#53813e", // Green
        "#ee7c3e", // Orange
        "#654b1e", // Brown
        "#4C82AC", // Dark Blue
        "#3a622c", // Dark Green
        "#cd6c35", // Rust
        "#be9c63", // Tan
        "#7FB3D5", // Light Sky Blue
        "#28B463", // Bright Green
        "#2874A6", // Navy Blue
        "#1E8449", // Forest Green
        "#F39C12", // Yellow-Orange
        "#A04000",  // Dark Rust
        "#000000", // Black
        "#FFFFFF", // White
    ];

    // New hierarchical career cluster structure
    const careerClusterHierarchy = {
        "Investing in the Future": {
            color: "#5b9dd0",
            clusters: ["Financial Services"]
        },
        "Connecting & Supporting Success": {
            color: "#53813e",
            clusters: ["Management & Entrepeneurship", "Digital Technology", "Marketing & Sales"]
        },
        "Caring for Communities": {
            color: "#ee7c3e",
            clusters: ["Education", "Healthcare & Human Services", "Public Service & Safety"]
        },
        "Cultivating Resources": {
            color: "#654b1e",
            clusters: ["Agriculture", "Energy & Natural Resources"]
        },
        "Building & Moving": {
            color: "#4C82AC",
            clusters: ["Construction", "Advanced Manufacturing", "Supply Chain & Transportation"]
        },
        "Creating & Experiencing": {
            color: "#3a622c",
            clusters: ["Hospitality, Events, & Tourism", "Arts, Entertainment, & Design"]
        }
    };

    // Define color palettes for child clusters within each parent
    const childColorPalettes = {
        "Investing in the Future": ["#5b9dd0"],
        "Connecting & Supporting Success": ["#53813e", "#7FB3D5", "#28B463"],
        "Caring for Communities": ["#ee7c3e", "#cd6c35", "#F39C12"],
        "Cultivating Resources": ["#654b1e", "#be9c63"],
        "Building & Moving": ["#4C82AC", "#2874A6", "#1E8449"],
        "Creating & Experiencing": ["#3a622c", "#A04000"]
    };

    // Aggregate data for parent categories (inner ring)
    const parentLabels = [];
    const parentData = [];
    const parentColors = [];

    // Aggregate data for child clusters (outer ring)
    const childLabels = [];
    const childData = [];
    const childColors = [];

    Object.entries(careerClusterHierarchy).forEach(([parentName, parentInfo]) => {
        let parentCount = 0;
        const clusters = parentInfo.clusters;
        const palette = childColorPalettes[parentName];

        clusters.forEach((cluster, index) => {
            // Count ELOs where the "Career Cluster" field matches this cluster name
            const count = filteredELOs.filter(elo => elo["Career Cluster"] === cluster).length;

            if (count > 0) {
                childLabels.push(cluster);
                childData.push(count);
                childColors.push(palette[index % palette.length]);
                parentCount += count;
            }
        });

        // Only add parent if it has data
        if (parentCount > 0) {
            parentLabels.push(parentName);
            parentData.push(parentCount);
            parentColors.push(parentInfo.color);
        }
    });


    // Create nested doughnut chart data (inner ring = parents, outer ring = children)
    const pieData = {
        labels: [...parentLabels, ...childLabels], // Combined labels for both rings
        datasets: [
            {
                label: "Parent Categories",
                data: [...parentData, ...Array(childLabels.length).fill(0)], // Parent data + zeros for child positions
                backgroundColor: [...parentColors, ...Array(childLabels.length).fill('transparent')],
                hoverOffset: 4,
                weight: 1, // Inner ring
            },
            {
                label: "Career Clusters",
                data: [...Array(parentLabels.length).fill(0), ...childData], // Zeros for parent positions + child data
                backgroundColor: [...Array(parentLabels.length).fill('transparent'), ...childColors],
                hoverOffset: 4,
                weight: 2, // Outer ring (larger weight = outer position)
            }
        ]
    };

    // bar data
    const barData = {
        labels: ['Interest Exploration', 'Pursuit Partnership', 'Work Based Learning', 'Community Embedded'],
        datasets: [
            {
                data: [
                    filteredELOs.filter(elo => elo["Interest Exploration"] !== "").length,
                    filteredELOs.filter(elo => elo["Pursuit Partnership"] !== "").length,
                    filteredELOs.filter(elo => elo["Work Based Learning"] !== "").length,
                    filteredELOs.filter(elo => elo["Community Embedded"] !== "").length
                ],
                backgroundColor: colors,
                hoverOffset: 4
            }
        ]
    };
    
    // bar options
    const barOptions = {
        responsive: true,
        plugins: {
            legend: {
                display: false, // Hide the legend
            },
            tooltip: {
                callbacks: {
                    label: function(tooltipItem) {
                        // Get the label from the dataset and the value for the tooltip
                        const datasetLabel = tooltipItem.dataset.label || '';
                        const value = Math.round(tooltipItem.raw); // Round the value to the nearest whole number
                        
                        // Format the tooltip label to include the data name and the count
                        return `${datasetLabel}: ${value}`;
                    }
                },
                titleColor: 'white',
                bodyColor: 'white',
                bodyFont: {
                    size: 15 // Set the font size for the tooltip body
                },
                titleFont: {
                    size: 15 // Set the font size for the tooltip title
                }
            },
            title: {
                display: false, // Hide the title
            }
        },
        scales: {
            x: {
                ticks: {
                    color: '#000000', // Set x-axis labels color to black
                    font: {
                        weight: 'bold', // Make x-axis labels bold
                        size: 14 // Adjust the font size for x-axis labels
                    },
                    stepSize: 1, // Ensure ticks are spaced by whole numbers
                    callback: function(value) {
                        return value; // Show whole numbers directly
                    }
                },
                // Optional: Add a min and max value if needed to limit the axis range
                // min: 0,
                // max: Math.max(...dataCounts)
            },
            y: {
                ticks: {
                    color: '#000000', // Set y-axis labels color to black
                    font: {
                        weight: 'bold', // Make y-axis labels bold
                        size: 14 // Adjust the font size for y-axis labels
                    }
                }
            }
        },
        indexAxis: 'y' // Set to horizontal bar chart
    };

    // pie/doughnut options for nested hierarchical chart
    const pieOptions = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2,
        layout: {
            padding: 0
        },
        plugins: {
            legend: {
                display: true,
                position: 'left',
                labels: {
                    color: '#000000',
                    font: {
                        weight: 'bold',
                        size: 12
                    },
                    boxWidth: 16,
                    padding: 8,
                    generateLabels: function(chart) {
                        const data = chart.data;
                        if (data.labels.length && data.datasets.length) {
                            // Only show child cluster labels (skip parent category labels)
                            const startIndex = parentLabels.length;
                            return data.labels.slice(startIndex).map((label, i) => {
                                const actualIndex = startIndex + i;
                                const dataset = data.datasets[1]; // Outer ring dataset
                                const backgroundColor = dataset.backgroundColor[actualIndex];
                                const meta = chart.getDatasetMeta(1);
                                const isHidden = meta.data[actualIndex] ? meta.data[actualIndex].hidden : false;
                                return {
                                    text: isHidden ? `\u0336${label.split('').join('\u0336')}\u0336` : label,
                                    fillStyle: isHidden ? '#cccccc' : backgroundColor,
                                    strokeStyle: isHidden ? '#cccccc' : backgroundColor,
                                    hidden: false, // Keep this false so the legend item is always visible
                                    index: actualIndex,
                                    datasetIndex: 1,
                                    fontColor: isHidden ? '#999999' : '#000000'
                                };
                            });
                        }
                        return [];
                    }
                },
                onClick: function(e, legendItem, legend) {
                    const index = legendItem.index;
                    const chart = legend.chart;
                    const meta = chart.getDatasetMeta(1);

                    // Toggle the hidden state of the clicked item
                    if (meta.data[index]) {
                        meta.data[index].hidden = !meta.data[index].hidden;
                    }

                    chart.update();
                }
            },
            tooltip: {
                callbacks: {
                    title: function(context) {
                        return context[0].label;
                    },
                    label: function(context) {
                        // Skip transparent/zero values
                        if (context.raw === 0) {
                            return null;
                        }

                        return `${context.raw} ELOs`;
                    }
                },
                // Set tooltip font size
                bodyFont: {
                    size: 15 // Set the font size for the tooltip body
                },
                titleFont: {
                    size: 15 // Set the font size for the tooltip title
                }
            },
        }
    };

    // labels for bar graph legend
    const interestExploration = ["Career Talk", "Field Site Visit", "Curiosity Lab", "Networking"];
    const wbl = ["Job Shadow", "Internship", "Externship", "Employment"];
    const community = ["Vision Coach", "Service Learning", "Civic Engagement", "Community Group/Class"];
    const pursuitPartnership = ["Micro Pursuit", "Mini Pursuit", "Personal Pursuit", "Partner Pursuit"];

    useEffect(() => {
        resetContext();
        fetchAllELOs();
        fetchAllPartners();
    }, [resetContext, fetchAllPartners, fetchAllELOs]);

    useEffect(() => {
        const fetchImages = async () => {
            const imagePromises = partners.map(async (partner) => {
                try {
                    const thumbnailUrl = await fetchContactUrl(partner["Name"]);
                    return {
                        partnerName: partner["Name"],
                        thumbnailUrl,
                    };
                } catch (error) {
                    console.error(`Error fetching files for partner ${partner["Name"]}:`, error);
                    return {
                        partnerName: partner["Name"],
                        thumbnailUrl: null,
                    };
                }
            });

            const results = await Promise.all(imagePromises);
            const imagesMap = results.reduce((acc, { partnerName, thumbnailUrl }) => {
                acc[partnerName] = thumbnailUrl;
                return acc;
            }, {});

            setImageUrlByPartner(imagesMap);
        };

        fetchImages();
    }, [partners, fetchContactUrl]);

    return (
        <Div
            bg="linear-gradient(135deg, #f5f7fa 0%, #c3cbbe 100%)"
            minH="100vh"
        >
            {/* Hero Section */}
            <Div
                bg="linear-gradient(135deg, #5b9dd0 0%, #53813e 100%)"
                p={{t:{xs:"6rem", md:"7rem"}, b:{xs:"2rem", md:"3rem"}, l:{xs:"1rem", md:"2rem"}, r:{xs:"1rem", md:"2rem"}}}
                style={{
                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    width: '100%',
                    boxSizing: 'border-box'
                }}
            >
                <Row justify="center" align="center">
                    <Image
                        src={require('../images/connect.png')}
                        h={{xs:"16rem", md:"20rem", lg:"24rem"}}
                        w="auto"
                        style={{
                            filter: 'brightness(1.1)',
                            maxWidth: '100%',
                            objectFit: 'contain'
                        }}
                    />
                </Row>
                {/* View Toggle */}
                <Row justify="center" align="center" m={{t:"1.5rem"}}>
                    <Div
                        bg="rgba(255,255,255,0.15)"
                        rounded="xl"
                        p={{l:"1.5rem", r:"1.5rem", t:"0.75rem", b:"0.75rem"}}
                        d="flex"
                        align="center"
                        style={{
                            backdropFilter: 'blur(10px)',
                            gap: '0.75rem'
                        }}
                    >
                        <Text
                            textColor={!isPartnerSearch ? "white" : "rgba(255,255,255,0.7)"}
                            textWeight="600"
                            textSize={{xs:"caption", md:"body"}}
                        >
                            Dashboard
                        </Text>
                        <Switch
                            checked={isPartnerSearch}
                            onClick={() => setIsPartnerSearch(!isPartnerSearch)}
                            activeColor="#ee7c3e"
                            inactiveColor="rgba(255,255,255,0.3)"
                            activeShadow="0"
                        />
                        <Text
                            textColor={isPartnerSearch ? "white" : "rgba(255,255,255,0.7)"}
                            textWeight="600"
                            textSize={{xs:"caption", md:"body"}}
                        >
                            Partners
                        </Text>
                    </Div>
                </Row>
            </Div>

            {/* Main Content Section */}
            <Div
                bg="#f8f9fa"
                style={{width: '100%', boxSizing: 'border-box'}}
            >
                {/* Description & Filter Area */}
                <Div maxW={{xs:"100%", lg:"1200px"}} m="auto" p={{t:{xs:"2rem", md:"3rem"}, b:{xs:"1rem", md:"2rem"}, l:{xs:"1rem", md:"2rem"}, r:{xs:"1rem", md:"2rem"}}} style={{width: '100%', boxSizing: 'border-box'}}>
                    {/* Description Text */}
                    <Div bg="white" rounded="xl" shadow="3" p={{xs:"1.5rem", md:"2rem"}} m={{b:"2rem"}} style={{width: '100%', boxSizing: 'border-box'}}>
                        {isPartnerSearch ? (
                            <Text
                                textColor="#4a5568"
                                textSize={{xs:"body", md:"subheader"}}
                                textAlign="center"
                                textWeight="500"
                                style={{lineHeight: '1.7'}}
                            >
                                Navigating Norris Connects is both efficient and user-friendly. Start by choosing one of the four main categories: Career Cluster, ELO Type, ELO Location, or Learning Hub to identify your area of exploration. Within each category, you can refine your search further by selecting a more specific subcategory that matches your interests or needs.
                            </Text>
                        ) : (
                            <Div>
                                <Text
                                    textColor="#4a5568"
                                    textSize={{xs:"body", md:"subheader"}}
                                    textAlign="center"
                                    textWeight="500"
                                    m={{b:"1rem"}}
                                    style={{lineHeight: '1.7'}}
                                >
                                    Welcome to Norris Connects, an exciting online platform where community members can dive into meaningful collaborations with the youth of the Norris School District. Our platform bridges interests and career goals across 14 dynamic career clusters.
                                </Text>
                                <Text
                                    textColor="#4a5568"
                                    textSize={{xs:"body", md:"subheader"}}
                                    textAlign="center"
                                    textWeight="500"
                                    style={{lineHeight: '1.7'}}
                                >
                                    At Norris School District, we're passionate about immersing our learners in real-world experiences by fostering strong connections with community partners.
                                </Text>
                            </Div>
                        )}
                    </Div>

                    {/* Filter Bar */}
                    <Div bg="white" rounded="xl" shadow="4" p={{xs:"0.75rem", md:"1.5rem"}} style={{width: '100%', boxSizing: 'border-box'}}>
                        <Row justify="center" align="stretch" style={{display: 'flex', flexWrap: 'nowrap', gap: '0.5rem'}}>
                            <Div
                                bg="#f8f9fa"
                                rounded="lg"
                                p={{t:{xs:"0.75rem", md:"1rem"}, b:{xs:"0.75rem", md:"1rem"}}}
                                textAlign="center"
                                cursor="pointer"
                                hoverBg="#e8eaed"
                                transition="all 0.2s ease"
                                onClick={() => setIsCareerClusterOpen(true)}
                                style={{border: careerCluster ? '2px solid #5b9dd0' : '2px solid transparent', flex: '1 1 0', minWidth: 0}}
                            >
                                <Div
                                    h={{xs:"2rem", md:"2.5rem"}}
                                    w={{xs:"2rem", md:"2.5rem"}}
                                    bg="#5b9dd0"
                                    rounded="circle"
                                    d="flex"
                                    align="center"
                                    justify="center"
                                    m={{l:"auto", r:"auto", b:"0.5rem"}}
                                >
                                    <Div
                                        h={{xs:"1rem", md:"1.2rem"}}
                                        w={{xs:"1rem", md:"1.2rem"}}
                                        bgImg={require('../images/Cluster.png')}
                                        bgSize="cover"
                                        bgPos="center center"
                                        style={{filter: 'brightness(10)'}}
                                    />
                                </Div>
                                <Text textColor="#2d3748" textWeight="600" textSize={{xs:"tiny", sm:"caption", md:"body"}} style={{whiteSpace: 'nowrap'}}>
                                    Career Cluster
                                </Text>
                            </Div>
                            <Div
                                bg="#f8f9fa"
                                rounded="lg"
                                p={{t:{xs:"0.75rem", md:"1rem"}, b:{xs:"0.75rem", md:"1rem"}}}
                                textAlign="center"
                                cursor="pointer"
                                hoverBg="#e8eaed"
                                transition="all 0.2s ease"
                                onClick={() => setIsEloTypeOpen(true)}
                                style={{border: eloType ? '2px solid #53813e' : '2px solid transparent', flex: '1 1 0', minWidth: 0}}
                            >
                                <Div
                                    h={{xs:"2rem", md:"2.5rem"}}
                                    w={{xs:"2rem", md:"2.5rem"}}
                                    bg="#53813e"
                                    rounded="circle"
                                    d="flex"
                                    align="center"
                                    justify="center"
                                    m={{l:"auto", r:"auto", b:"0.5rem"}}
                                >
                                    <Div
                                        h={{xs:"1rem", md:"1.2rem"}}
                                        w={{xs:"1rem", md:"1.2rem"}}
                                        bgImg={require('../images/about.png')}
                                        bgSize="cover"
                                        bgPos="center center"
                                        style={{filter: 'brightness(10)'}}
                                    />
                                </Div>
                                <Text textColor="#2d3748" textWeight="600" textSize={{xs:"tiny", sm:"caption", md:"body"}} style={{whiteSpace: 'nowrap'}}>
                                    ELO Type
                                </Text>
                            </Div>
                            <Div
                                bg="#f8f9fa"
                                rounded="lg"
                                p={{t:{xs:"0.75rem", md:"1rem"}, b:{xs:"0.75rem", md:"1rem"}}}
                                textAlign="center"
                                cursor="pointer"
                                hoverBg="#e8eaed"
                                transition="all 0.2s ease"
                                onClick={() => setIsEloLocationOpen(true)}
                                style={{border: eloLocation ? '2px solid #ee7c3e' : '2px solid transparent', flex: '1 1 0', minWidth: 0}}
                            >
                                <Div
                                    h={{xs:"2rem", md:"2.5rem"}}
                                    w={{xs:"2rem", md:"2.5rem"}}
                                    bg="#ee7c3e"
                                    rounded="circle"
                                    d="flex"
                                    align="center"
                                    justify="center"
                                    m={{l:"auto", r:"auto", b:"0.5rem"}}
                                >
                                    <Icon name="Location" color="white" size={{xs:"16px", md:"20px"}} />
                                </Div>
                                <Text textColor="#2d3748" textWeight="600" textSize={{xs:"tiny", sm:"caption", md:"body"}} style={{whiteSpace: 'nowrap'}}>
                                    ELO Location
                                </Text>
                            </Div>
                            <Div
                                bg="#f8f9fa"
                                rounded="lg"
                                p={{t:{xs:"0.75rem", md:"1rem"}, b:{xs:"0.75rem", md:"1rem"}}}
                                textAlign="center"
                                cursor="pointer"
                                hoverBg="#e8eaed"
                                transition="all 0.2s ease"
                                onClick={() => setIsEloOpen(true)}
                                style={{border: elo ? '2px solid #654b1e' : '2px solid transparent', flex: '1 1 0', minWidth: 0}}
                            >
                                <Div
                                    h={{xs:"2rem", md:"2.5rem"}}
                                    w={{xs:"2rem", md:"2.5rem"}}
                                    bg="#654b1e"
                                    rounded="circle"
                                    d="flex"
                                    align="center"
                                    justify="center"
                                    m={{l:"auto", r:"auto", b:"0.5rem"}}
                                >
                                    <Icon name="Home" color="white" size={{xs:"16px", md:"20px"}} />
                                </Div>
                                <Text textColor="#2d3748" textWeight="600" textSize={{xs:"tiny", sm:"caption", md:"body"}} style={{whiteSpace: 'nowrap'}}>
                                    Learning Hub
                                </Text>
                            </Div>
                        </Row>
                    </Div>
                </Div>

                    <Modal
                        isOpen={isCareerClusterOpen}
                        onClose={() => setIsCareerClusterOpen(false)}
                        align="center"
                        rounded="xl"
                        bg="white"
                        p={{xs:"1.5rem", md:"2rem"}}
                    >
                        <Col
                            flexDir="column"
                            align="center"
                            justify="center"
                        >
                            {Object.entries(careerClusterHierarchy).map(([parentName, parentInfo]) => (
                                <React.Fragment key={parentName}>
                                    <Row
                                        justify="center"
                                        p={{t:".5rem", b:"1rem"}}
                                    >
                                        <Text
                                            textColor="black"
                                            textSize="subheader"
                                            textWeight="700"
                                            w="100%"
                                            d="block"
                                            textAlign="center"
                                        >
                                            {parentName}
                                        </Text>
                                    </Row>
                                    {parentInfo.clusters.map((cluster) => (
                                        <Row
                                            key={cluster}
                                            justify="center"
                                            p={{b:"1rem"}}
                                        >
                                            <Link
                                                onClick={() => onSelectCareerCluster(cluster)}
                                                textColor="black"
                                                textSize="body"
                                                textWeight="300"
                                                w="100%"
                                                d="block"
                                                style={{ textDecoration: 'none', textAlign: 'center' }}
                                                className={styles.modalLink}
                                            >
                                                {cluster}
                                            </Link>
                                        </Row>
                                    ))}
                                </React.Fragment>
                            ))}
                        </Col>
                    </Modal>
                    <Modal
                        isOpen={isEloTypeOpen}
                        onClose={() => setIsEloTypeOpen(false)}
                        align="center"
                        rounded="xl"
                        bg="white"
                        p={{xs:"1.5rem", md:"2rem"}}
                    >
                        <Col
                            flexDir="column"
                            align="center"
                            justify="center"
                        >
                            <Row
                                justify="center"
                                p={{t:".5rem", b:"1rem"}}
                            >
                                <Text
                                    textColor="black"
                                    textSize="subheader"
                                    textWeight="700"
                                    w="100%"
                                    d="block"
                                    textAlign="center"
                                >
                                    Interest Exploration
                                </Text>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Career Talk")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Career Talk
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Field Site Visit")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Field Site Visit
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Curiosity Lab")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Curiosity Lab
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Networking")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Networking
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{t:".5rem", b:"1rem"}}
                            >
                                <Text
                                    textColor="black"
                                    textSize="subheader"
                                    textWeight="700"
                                    w="100%"
                                    d="block"
                                    textAlign="center"
                                >
                                    Pursuit Partnership
                                </Text>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Micro Pursuit")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Micro Pursuit
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Mini Pursuit")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Mini Pursuit
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Personal Pursuit")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Personal Pursuit
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Partner Pursuit")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Partner Pursuit
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{t:".5rem", b:"1rem"}}
                            >
                                <Text
                                    textColor="black"
                                    textSize="subheader"
                                    textWeight="700"
                                    w="100%"
                                    d="block"
                                    textAlign="center"
                                >
                                    Work Based Learning
                                </Text>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Job Shadow")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Job Shadow
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Internship")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Internship
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Externship")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Externship
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Employment")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Employment
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{t:".5rem", b:"1rem"}}
                            >
                                <Text
                                    textColor="black"
                                    textSize="subheader"
                                    textWeight="700"
                                    w="100%"
                                    d="block"
                                    textAlign="center"
                                >
                                    Community Embedded
                                </Text>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Vision Coach")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Vision Coach
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Service Learning")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Service Learning
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Civic Engagement")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}

                                >
                                    Civic Engagement
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloType("Community Group/Class")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Community Group/Class
                                </Link>
                            </Row>
                        </Col>
                    </Modal>
                    <Modal
                        isOpen={isEloLocationOpen}
                        onClose={() => setIsEloLocationOpen(false)}
                        align="center"
                        rounded="xl"
                        bg="white"
                        p={{xs:"1.5rem", md:"2rem"}}
                    >
                        <Col
                            flexDir="column"
                            align="center"
                            justify="center"
                        >
                            <Row
                                justify="center"
                                p={{t:"1rem", b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloLocation("Norris Mukwonago")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Norris, Mukwonago Campus
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{ b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloLocation("Learn Milwaukee")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Achieve/Learn MKE
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectEloLocation("Virtual")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Virtual
                                </Link>
                            </Row>
                        </Col>
                    </Modal>
                    <Modal
                        isOpen={isEloOpen}
                        onClose={() => setIsEloOpen(false)}
                        align="center"
                        rounded="xl"
                        bg="white"
                        p={{xs:"1.5rem", md:"2rem"}}
                    >
                        <Col
                            flexDir="column"
                            align="center"
                            justify="center"
                        >
                            <Row
                                justify="center"
                                p={{t:"1rem", b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectElo("Home Base")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Home Base
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{ b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectElo("Learning Hub")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Learning Hub
                                </Link>
                            </Row>
                            <Row
                                justify="center"
                                p={{b:"1rem"}}
                            >
                                <Link
                                    onClick={() => onSelectElo("Field Site")}
                                    textColor="black"
                                    textSize="body"
                                    textWeight="300"
                                    w="100%"
                                    d="block"
                                    style={{ textDecoration: 'none' }}
                                    className={styles.modalLink}
                                >
                                    Field Site
                                </Link>
                            </Row>
                        </Col>
                    </Modal>

                    {/* Active Filter Tags */}
                    {selectedFilters.length > 0 && (
                        <Div p={{t:"1rem", b:"1rem"}} style={{width: '100%', boxSizing: 'border-box'}}>
                            <Div maxW={{xs:"100%", lg:"1200px"}} m="auto" p={{l:{xs:"1rem", md:"2rem"}, r:{xs:"1rem", md:"2rem"}}}>
                                <Row justify="center" flexWrap="wrap" style={{gap: '0.5rem'}}>
                                    {selectedFilters.map((filter, idx) => (
                                        <Button
                                            key={idx}
                                            bg="white"
                                            textColor="#2d3748"
                                            rounded="lg"
                                            shadow="2"
                                            onClick={() => onSpliceSelectedFilters(filter)}
                                            suffix={
                                                <Icon
                                                    name="Cross"
                                                    size="14px"
                                                    color="#718096"
                                                    m={{ l: "0.5rem" }}
                                                />
                                            }
                                            p={{l:"1rem", r:"0.75rem", t:"0.5rem", b:"0.5rem"}}
                                            textSize="caption"
                                            textWeight="600"
                                            style={{border: '1px solid #e2e8f0'}}
                                        >
                                            {filter}
                                        </Button>
                                    ))}
                                </Row>
                            </Div>
                        </Div>
                    )}

                    {/* Partner Search View */}
                    {isPartnerSearch ? (
                        <Div style={{width: '100%', boxSizing: 'border-box'}}>
                            {loading ? (
                                <Div>
                                    <Row
                                        justify="center"
                                        p={{t:"10rem", b:"12rem"}}
                                    >
                                        <Icon name="Loading3" size="4rem" color="#5b9dd0" />
                                    </Row>
                                </Div>
                            ) : (
                                <Div maxW={{xs:"100%", lg:"1400px"}} m="auto" p={{l:{xs:"1rem", md:"2rem"}, r:{xs:"1rem", md:"2rem"}, t:"1rem", b:"3rem"}} style={{width: '100%', boxSizing: 'border-box'}}>
                                    {/* Search Bar */}
                                    <Div p={{b:"2rem"}} style={{width: '100%'}}>
                                        <Input
                                            placeholder="Search by keyword, ZIP code, or county..."
                                            borderColor="#5b9dd0"
                                            focusBorderColor="#4a8bb8"
                                            border="2px solid"
                                            w="100%"
                                            rounded="lg"
                                            p={{l:"1.5rem", r:"1.5rem", t:"0.75rem", b:"0.75rem"}}
                                            bg="white"
                                            shadow="2"
                                            textSize="body"
                                            onChange={(e) => handleInputChange(e.target.value)}
                                        />
                                    </Div>

                                    {/* Partner Thank You Message */}
                                    <Div bg="white" rounded="xl" shadow="3" p={{xs:"1.5rem", md:"2rem"}} m={{b:"2rem"}} style={{width: '100%', boxSizing: 'border-box'}}>
                                        <Text
                                            textColor="#4a5568"
                                            textSize={{xs:"body", md:"subheader"}}
                                            textAlign="center"
                                            textWeight="500"
                                            style={{lineHeight: '1.7'}}
                                        >
                                            We sincerely thank our current partners for their steadfast support and dedication to providing lifelong resources for our learners. Through our collaborative efforts, we have been able to offer learners enriching experiences that spark their curiosity and prepare them to become active, successful members of their communities.
                                        </Text>
                                    </Div>

                                    {/* Partner Grid */}
                                    {filteredPartners.length === 0 ? (
                                        <Div bg="white" rounded="xl" shadow="3" p={{t:"3rem", b:"3rem", l:"2rem", r:"2rem"}} textAlign="center" style={{width: '100%'}}>
                                            <Icon name="Search" size="3rem" color="#cbd5e0" m={{b:"1rem"}} />
                                            <Text
                                                textColor="#718096"
                                                textWeight="600"
                                                textSize="subheader"
                                            >
                                                No matching results. Try a different search.
                                            </Text>
                                        </Div>
                                    ) : (
                                        <Row justify="center" flexWrap="wrap" style={{gap: '1.5rem'}}>
                                            {filteredPartners.map((partner, idx) => {
                                                const imageUrl = imageUrlByPartner[partner["Name"]] || '';
                                                const isImageLoaded = loadedImages[partner["Name"]];
                                                return (
                                                    <Div key={idx}>
                                                        <Link
                                                            to={`/partner/${partner["Name"]}`}
                                                            style={{ textDecoration: 'none' }}
                                                        >
                                                            <Div
                                                                bg="white"
                                                                rounded="xl"
                                                                shadow="3"
                                                                hoverShadow="5"
                                                                transition="all 0.3s ease"
                                                                style={{
                                                                    overflow: 'hidden',
                                                                    width: '16rem',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <Div style={{ position: "relative", width: "16rem", height: "12rem" }}>
                                                                    {imageUrl && (
                                                                        <Image
                                                                            src={imageUrl}
                                                                            h="12rem"
                                                                            w="16rem"
                                                                            alt="Partner Photo"
                                                                            style={{
                                                                                objectFit:'cover',
                                                                                opacity: isImageLoaded ? 1 : 0,
                                                                                transition: 'opacity 0.3s ease-in-out',
                                                                                position: 'absolute',
                                                                                top: 0,
                                                                                left: 0
                                                                            }}
                                                                            onLoad={() => setLoadedImages(prev => ({...prev, [partner["Name"]]: true}))}
                                                                        />
                                                                    )}
                                                                    {(!imageUrl || !isImageLoaded) && (
                                                                        <Div
                                                                            h="12rem"
                                                                            w="16rem"
                                                                            bg="linear-gradient(135deg, #e8eaed 0%, #d1d5db 100%)"
                                                                            style={{
                                                                                position: 'absolute',
                                                                                top: 0,
                                                                                left: 0,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center'
                                                                            }}
                                                                        >
                                                                            <Icon name="Loading3" size="2.5rem" color="#5b9dd0" />
                                                                        </Div>
                                                                    )}
                                                                    {/* Gradient overlay */}
                                                                    <Div
                                                                        style={{
                                                                            position: 'absolute',
                                                                            bottom: 0,
                                                                            left: 0,
                                                                            right: 0,
                                                                            height: '50%',
                                                                            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                                                                            zIndex: 1
                                                                        }}
                                                                    />
                                                                </Div>
                                                                <Div p={{l:"1rem", r:"1rem", t:"0.75rem", b:"0.75rem"}} bg="white">
                                                                    <Text
                                                                        textWeight="700"
                                                                        textColor="#2d3748"
                                                                        textSize="body"
                                                                        style={{
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        {partner["Name"]}
                                                                    </Text>
                                                                    {partner["Contact Name"] && (
                                                                        <Text
                                                                            textWeight="500"
                                                                            textColor="#718096"
                                                                            textSize="caption"
                                                                            style={{
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                        >
                                                                            {partner["Contact Name"]}
                                                                        </Text>
                                                                    )}
                                                                </Div>
                                                            </Div>
                                                        </Link>
                                                    </Div>
                                                );
                                            })}
                                        </Row>
                                    )}
                                </Div>
                            )}
                        </Div>
                    ) : (
                        <Div style={{overflow: 'hidden', width: '100%'}}>
                            {loadingAllELO ? (
                                <Div>
                                    <Row
                                        justify="center"
                                        p={{t:"10rem",b:"12rem"}}
                                    >
                                        <Icon name="Loading3" size="4rem" color="#5b9dd0" />
                                    </Row>
                                </Div>
                            ) : (
                                <Div style={{overflow: 'hidden', width: '100%'}}>
                                    {/* Engagement Stats Section */}
                                    <Div p={{t:{xs:"2rem", md:"4rem"}, b:{xs:"2rem", md:"4rem"}}} style={{overflow: 'hidden', width: '100%'}}>
                                        <Div maxW={{xs:"100%", lg:"1400px"}} m="auto" p={{l:{xs:"1rem", md:"2rem"}, r:{xs:"1rem", md:"2rem"}}} style={{width: '100%', boxSizing: 'border-box'}}>
                                            <Text
                                                textColor="#2d3748"
                                                textWeight="700"
                                                textSize={{xs:"subheader", sm:"heading", md:"display1"}}
                                                textAlign="center"
                                                p={{b:{xs:"1.5rem", md:"2rem"}}}
                                            >
                                                Engagement Dashboard
                                            </Text>
                                            <Row justify="center" p={{b:{xs:"2rem", md:"3rem"}}} flexWrap="wrap" style={{maxWidth: '100%'}}>
                                                <Dropdown
                                                    isOpen={showTermDropdown}
                                                    onClick={() => setShowTermDropdown(!showTermDropdown)}
                                                    menu={termList}
                                                    w={{xs:"calc(50% - 0.5rem)", sm:"45%", md:"30%", lg:"20%"}}
                                                    m={{xs:".25rem", md:".5rem"}}
                                                    textWeight="600"
                                                    borderColor="#5b9dd0"
                                                    focusBorderColor="#5b9dd0"
                                                    bg="white"
                                                    border="2px solid"
                                                    textColor="#2d3748"
                                                    rounded="lg"
                                                    shadow="2"
                                                    textSize={{xs:"caption", md:"body"}}
                                                >
                                                    {selectedTerm}
                                                </Dropdown>
                                                <Dropdown
                                                    isOpen={showYearDropdown}
                                                    onClick={() => setShowYearDropdown(!showYearDropdown)}
                                                    menu={sortList}
                                                    w={{xs:"calc(50% - 0.5rem)", sm:"45%", md:"30%", lg:"20%"}}
                                                    m={{xs:".25rem", md:".5rem"}}
                                                    textWeight="600"
                                                    borderColor="#5b9dd0"
                                                    focusBorderColor="#5b9dd0"
                                                    bg="white"
                                                    border="2px solid"
                                                    textColor="#2d3748"
                                                    rounded="lg"
                                                    shadow="2"
                                                    textSize={{xs:"caption", md:"body"}}
                                                >
                                                    {selectedYear}
                                                </Dropdown>
                                            </Row>
                                            <Row justify="center" p={{b:"1rem"}} flexWrap="wrap" style={{maxWidth: '100%', gap: '1rem'}}>
                                                <Col size={{xs:"12", md:"5"}} style={{maxWidth: '100%', boxSizing: 'border-box'}}>
                                                    <Div
                                                        bg="linear-gradient(135deg, #5b9dd0 0%, #4a8bb8 100%)"
                                                        rounded="xl"
                                                        p={{xs:"1rem", sm:"1.5rem", md:"2rem"}}
                                                        textAlign="center"
                                                        shadow="4"
                                                        style={{width: '100%', boxSizing: 'border-box', minWidth: 0}}
                                                    >
                                                        <Text
                                                            textColor="white"
                                                            textSize={{xs:"title", sm:"heading", md:"display1", lg:"display2"}}
                                                            textWeight="800"
                                                            p={{b:".5rem"}}
                                                            style={{wordBreak: 'break-word', overflow: 'hidden'}}
                                                        >
                                                            {sumLearnersServed(filteredELOs)}
                                                        </Text>
                                                        <Text
                                                            textColor="white"
                                                            textSize={{xs:"caption", sm:"body", md:"subheader", lg:"title"}}
                                                            textWeight="600"
                                                            style={{opacity: 0.95, wordBreak: 'break-word'}}
                                                        >
                                                            Learners Served
                                                        </Text>
                                                    </Div>
                                                </Col>
                                                <Col size={{xs:"12", md:"5"}} style={{maxWidth: '100%', boxSizing: 'border-box'}}>
                                                    <Div
                                                        bg="linear-gradient(135deg, #53813e 0%, #3f6630 100%)"
                                                        rounded="xl"
                                                        p={{xs:"1rem", sm:"1.5rem", md:"2rem"}}
                                                        textAlign="center"
                                                        shadow="4"
                                                        style={{width: '100%', boxSizing: 'border-box', minWidth: 0}}
                                                    >
                                                        <Text
                                                            textColor="white"
                                                            textSize={{xs:"title", sm:"heading", md:"display1", lg:"display2"}}
                                                            textWeight="800"
                                                            p={{b:".5rem"}}
                                                            style={{wordBreak: 'break-word', overflow: 'hidden'}}
                                                        >
                                                            {filteredELOs?.length}
                                                        </Text>
                                                        <Text
                                                            textColor="white"
                                                            textSize={{xs:"caption", sm:"body", md:"subheader", lg:"title"}}
                                                            textWeight="600"
                                                            style={{opacity: 0.95, wordBreak: 'break-word'}}
                                                        >
                                                            ELOs Completed
                                                        </Text>
                                                    </Div>
                                                </Col>
                                            </Row>
                                            {filteredELOs.length > 0 && (
                                                <>
                                                    <Row justify="center" m={{t:"4rem", b:"4rem"}}>
                                                        <Col size="12">
                                                            <Div bg="white" rounded="xl" shadow="4" p={{xs:"2rem", md:"3rem"}} style={{overflow: 'hidden', width: '100%', boxSizing: 'border-box'}}>
                                                                <Text textColor="#2d3748" textSize={{xs:"subheader", md:"title"}} textWeight="700" textAlign="center" p={{b:"2rem"}}>
                                                                    Career Clusters Distribution
                                                                </Text>
                                                                <Div style={{maxWidth: '100%', width: '100%', margin: '0 auto', position: 'relative', height: 'auto'}}>
                                                                    <Doughnut data={pieData} options={pieOptions} />
                                                                </Div>
                                                                <Row justify="center" p={{t:"2rem"}}>
                                                                    <a
                                                                        href="https://docs.google.com/document/d/1Yk9eiakHZpQgDXTGuXnKpJ2lVstjYXwfyfnSnbV8Jzs/edit?usp=sharing"
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        style={{textDecoration: 'none'}}
                                                                    >
                                                                        <Button
                                                                            bg="#5b9dd0"
                                                                            hoverBg="#4a8bb8"
                                                                            textColor="white"
                                                                            rounded="lg"
                                                                            p={{l:"1.5rem", r:"1.5rem"}}
                                                                            shadow="2"
                                                                        >
                                                                            View Career Cluster Descriptions
                                                                        </Button>
                                                                    </a>
                                                                </Row>
                                                            </Div>
                                                        </Col>
                                                    </Row>

                                                    <Row justify="center" p={{t:"1rem", b:"3rem"}}>
                                                        <Col size="12">
                                                            <Div bg="white" rounded="xl" shadow="4" p={{xs:"1rem", md:"2rem"}} style={{overflow: 'hidden', width: '100%', boxSizing: 'border-box'}}>
                                                                <Text textColor="#2d3748" textSize={{xs:"subheader", md:"title"}} textWeight="700" textAlign="center" p={{b:"1.5rem"}}>
                                                                    ELO Types Breakdown
                                                                </Text>
                                                                <Div style={{position: 'relative', height: 'auto', width: '100%', maxWidth: '100%'}}>
                                                                    <Bar data={barData} options={barOptions} />
                                                                </Div>
                                                                {/* ELO Types Legend */}
                                                                <Div bg="#f8f9fa" rounded="xl" p={{xs:"1rem", md:"1.5rem"}} m={{t:"1.5rem"}} style={{width: '100%', boxSizing: 'border-box'}}>
                                                                    <Row flexWrap="wrap" justify="center">
                                                                        <Col size={{xs:"6", sm:"3"}} p="0.5rem">
                                                                            <Div textAlign="center">
                                                                                <Div bg="#5b9dd0" rounded="circle" h="2.5rem" w="2.5rem" d="flex" align="center" justify="center" m={{l:"auto", r:"auto", b:"0.5rem"}}>
                                                                                    <Text textColor="white" textWeight="700" textSize="caption">IE</Text>
                                                                                </Div>
                                                                                {interestExploration.map((interest, idx) => (
                                                                                    <Text key={idx} textColor="#4a5568" textSize="caption" m={{b:"0.25rem"}}>{interest}</Text>
                                                                                ))}
                                                                            </Div>
                                                                        </Col>
                                                                        <Col size={{xs:"6", sm:"3"}} p="0.5rem">
                                                                            <Div textAlign="center">
                                                                                <Div bg="#53813e" rounded="circle" h="2.5rem" w="2.5rem" d="flex" align="center" justify="center" m={{l:"auto", r:"auto", b:"0.5rem"}}>
                                                                                    <Text textColor="white" textWeight="700" textSize="caption">PP</Text>
                                                                                </Div>
                                                                                {pursuitPartnership.map((interest, idx) => (
                                                                                    <Text key={idx} textColor="#4a5568" textSize="caption" m={{b:"0.25rem"}}>{interest}</Text>
                                                                                ))}
                                                                            </Div>
                                                                        </Col>
                                                                        <Col size={{xs:"6", sm:"3"}} p="0.5rem">
                                                                            <Div textAlign="center">
                                                                                <Div bg="#ee7c3e" rounded="circle" h="2.5rem" w="2.5rem" d="flex" align="center" justify="center" m={{l:"auto", r:"auto", b:"0.5rem"}}>
                                                                                    <Text textColor="white" textWeight="700" textSize="caption">WBL</Text>
                                                                                </Div>
                                                                                {wbl.map((interest, idx) => (
                                                                                    <Text key={idx} textColor="#4a5568" textSize="caption" m={{b:"0.25rem"}}>{interest}</Text>
                                                                                ))}
                                                                            </Div>
                                                                        </Col>
                                                                        <Col size={{xs:"6", sm:"3"}} p="0.5rem">
                                                                            <Div textAlign="center">
                                                                                <Div bg="#654b1e" rounded="circle" h="2.5rem" w="2.5rem" d="flex" align="center" justify="center" m={{l:"auto", r:"auto", b:"0.5rem"}}>
                                                                                    <Text textColor="white" textWeight="700" textSize="caption">CE</Text>
                                                                                </Div>
                                                                                {community.map((interest, idx) => (
                                                                                    <Text key={idx} textColor="#4a5568" textSize="caption" m={{b:"0.25rem"}}>{interest}</Text>
                                                                                ))}
                                                                            </Div>
                                                                        </Col>
                                                                    </Row>
                                                                </Div>
                                                                <Row justify="center" p={{t:"1.5rem"}}>
                                                                    <a
                                                                        href="https://docs.google.com/document/d/1C9uEqEpTMI3w0Ww3JMz-CRBNZ0ZWc1fLuz_dJlAcYNo/edit?usp=sharing"
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        style={{textDecoration: 'none'}}
                                                                    >
                                                                        <Button
                                                                            bg="#53813e"
                                                                            hoverBg="#3f6630"
                                                                            textColor="white"
                                                                            rounded="lg"
                                                                            p={{l:"1.5rem", r:"1.5rem"}}
                                                                            shadow="2"
                                                                        >
                                                                            View ELO Descriptions
                                                                        </Button>
                                                                    </a>
                                                                </Row>
                                                            </Div>
                                                        </Col>
                                                    </Row>
                                                </>
                                            )}
                                        </Div>
                                    </Div>
                                </Div>
                            )}
                        </Div>
                    )}
            </Div>
        </Div>
    );
}

export default HomePage;